import React, {
    useEffect,
    useMemo,
    useState
} from "react";

import { Link } from "react-router-dom";

import API_BASE_URL from "../config/api";


function Users() {

    /*
    |--------------------------------------------------------------------------
    | API
    |--------------------------------------------------------------------------
    */

    const API_URL =
        `${API_BASE_URL}/admin/users`;


    /*
    |--------------------------------------------------------------------------
    | State
    |--------------------------------------------------------------------------
    */

    const [users, setUsers] = useState([]);

    const [papers, setPapers] = useState([]);

    const [loading, setLoading] = useState(true);

    const [error, setError] = useState("");

    const [search, setSearch] = useState("");

    const [currentPage, setCurrentPage] = useState(1);


    /*
    |--------------------------------------------------------------------------
    | Pagination
    |--------------------------------------------------------------------------
    */

    const itemsPerPage = 10;


    /*
    |--------------------------------------------------------------------------
    | Fetch Users
    |--------------------------------------------------------------------------
    */

    const fetchUsers = async () => {

        try {

            setLoading(true);

            setError("");


            const response = await fetch(
                API_URL,
                {
                    method: "GET",

                    headers: {
                        Accept:
                            "application/json"
                    },

                    credentials: "include"
                }
            );


            /*
            |--------------------------------------------------------------------------
            | Unauthorized
            |--------------------------------------------------------------------------
            */

            if (response.status === 401) {

                throw new Error(
                    "Admin authentication failed. Please login again."
                );

            }


            /*
            |--------------------------------------------------------------------------
            | Server Error
            |--------------------------------------------------------------------------
            */

            if (!response.ok) {

                throw new Error(
                    `Server Error: ${response.status}`
                );

            }


            /*
            |--------------------------------------------------------------------------
            | JSON
            |--------------------------------------------------------------------------
            */

            const data =
                await response.json();


            console.log(
                "Users API Response:",
                data
            );


            /*
            |--------------------------------------------------------------------------
            | Success
            |--------------------------------------------------------------------------
            */

            if (data.success) {

                setUsers(
                    Array.isArray(data.users)
                        ? data.users
                        : []
                );


                setPapers(
                    Array.isArray(data.papers)
                        ? data.papers
                        : []
                );

            } else {

                setError(
                    data.message ||
                    "Unable to fetch users."
                );

            }

        } catch (error) {

            console.error(
                "Users API Error:",
                error
            );


            setError(
                error.message ||
                "Unable to connect with server."
            );

        } finally {

            setLoading(false);

        }

    };


    /*
    |--------------------------------------------------------------------------
    | Fetch On Load
    |--------------------------------------------------------------------------
    */

    useEffect(() => {

        fetchUsers();

    }, []);


    /*
    |--------------------------------------------------------------------------
    | Search
    |--------------------------------------------------------------------------
    */

    const filteredUsers = useMemo(() => {

        const value =
            search
                .trim()
                .toLowerCase();


        if (!value) {

            return users;

        }


        return users.filter(
            (user) => {

                const fullname =
                    String(
                        user.fullname || ""
                    ).toLowerCase();


                const userId =
                    String(
                        user.user_id || ""
                    ).toLowerCase();


                const purchased =
                    Array.isArray(
                        user.purchased_papers
                    )
                        ? user.purchased_papers
                            .join(" ")
                            .toLowerCase()
                        : "";


                return (
                    fullname.includes(value) ||
                    userId.includes(value) ||
                    purchased.includes(value)
                );

            }
        );

    }, [users, search]);


    /*
    |--------------------------------------------------------------------------
    | Pagination
    |--------------------------------------------------------------------------
    */

    const totalPages =
        Math.ceil(
            filteredUsers.length /
            itemsPerPage
        );


    const startIndex =
        (currentPage - 1) *
        itemsPerPage;


    const paginatedUsers =
        filteredUsers.slice(
            startIndex,
            startIndex + itemsPerPage
        );


    /*
    |--------------------------------------------------------------------------
    | Search Handler
    |--------------------------------------------------------------------------
    */

    const handleSearch = (event) => {

        setSearch(
            event.target.value
        );

        setCurrentPage(1);

    };


    /*
    |--------------------------------------------------------------------------
    | Previous
    |--------------------------------------------------------------------------
    */

    const handlePrevious = () => {

        if (currentPage > 1) {

            setCurrentPage(
                currentPage - 1
            );

        }

    };


    /*
    |--------------------------------------------------------------------------
    | Next
    |--------------------------------------------------------------------------
    */

    const handleNext = () => {

        if (
            currentPage < totalPages
        ) {

            setCurrentPage(
                currentPage + 1
            );

        }

    };


    /*
    |--------------------------------------------------------------------------
    | Loading
    |--------------------------------------------------------------------------
    */

    if (loading) {

        return (

            <div
                className="
                    container-fluid
                    d-flex
                    justify-content-center
                    align-items-center
                "
                style={{
                    minHeight: "400px"
                }}
            >

                <div className="text-center">

                    <div
                        className="
                            spinner-border
                            text-primary
                        "
                        role="status"
                    >
                    </div>


                    <div
                        className="
                            mt-3
                            text-muted
                        "
                    >
                        Loading users...
                    </div>

                </div>

            </div>

        );

    }


    /*
    |--------------------------------------------------------------------------
    | Main
    |--------------------------------------------------------------------------
    */

    return (

        <div className="container-fluid">


            {/* =====================================================
                Page Header
            ===================================================== */}

            <div
                className="
                    d-flex
                    justify-content-between
                    align-items-center
                    mb-4
                "
            >

                <div>

                    <h3 className="mb-1">
                        Users
                    </h3>


                    <p className="text-muted mb-0">
                        Users and paper purchase details
                    </p>

                </div>


                {/* =================================================
                    Top Buttons
                ================================================= */}

                <div className="d-flex gap-2">


                    {/* Refresh */}

                    <button
                        type="button"
                        className="btn btn-outline-primary"
                        onClick={fetchUsers}
                    >

                        <i
                            className="
                                fa
                                fa-refresh
                                me-2
                            "
                        ></i>

                        Refresh

                    </button>


                    {/* Add User */}

                    <Link
                        to="/add-user"
                        className="btn btn-primary"
                    >

                        <i
                            className="
                                fa
                                fa-plus
                                me-2
                            "
                        ></i>

                        Add User

                    </Link>

                </div>

            </div>


            {/* =====================================================
                Error
            ===================================================== */}

            {error && (

                <div
                    className="
                        alert
                        alert-danger
                        d-flex
                        justify-content-between
                        align-items-center
                    "
                >

                    <span>
                        {error}
                    </span>


                    <button
                        type="button"
                        className="
                            btn
                            btn-sm
                            btn-danger
                        "
                        onClick={fetchUsers}
                    >
                        Try Again
                    </button>

                </div>

            )}


            {/* =====================================================
                Card
            ===================================================== */}

            <div className="card shadow-sm">


                {/* =================================================
                    Card Header
                ================================================= */}

                <div className="card-header bg-white">

                    <div className="row align-items-center">


                        {/* Search */}

                        <div className="col-md-5">

                            <div
                                className="input-group"
                            >

                                <span
                                    className="
                                        input-group-text
                                        bg-white
                                    "
                                >

                                    <i
                                        className="
                                            fa
                                            fa-search
                                        "
                                    ></i>

                                </span>


                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="
                                        Search name, user ID or paper...
                                    "
                                    value={search}
                                    onChange={
                                        handleSearch
                                    }
                                />

                            </div>

                        </div>


                        {/* Total */}

                        <div
                            className="
                                col-md-7
                                text-md-end
                                mt-3
                                mt-md-0
                            "
                        >

                            <span className="text-muted">
                                Total Users:
                            </span>


                            <strong className="ms-2">
                                {filteredUsers.length}
                            </strong>


                            <span
                                className="
                                    text-muted
                                    ms-4
                                "
                            >
                                Enabled Papers:
                            </span>


                            <strong className="ms-2">
                                {papers.length}
                            </strong>

                        </div>

                    </div>

                </div>


                {/* =================================================
                    Table
                ================================================= */}

                <div className="card-body p-0">

                    <div
                        className="table-responsive"
                        style={{
                            maxHeight: "320px"
                        }}
                    >

                        <table
                            className="
                                table
                                table-bordered
                                table-hover
                                align-middle
                                mb-0
                            "
                        >


                            {/* =================================================
                                Header
                            ================================================= */}

                            <thead
                                className="table-light"
                                style={{
                                    position: "sticky",
                                    top: 0,
                                    zIndex: 5
                                }}
                            >

                                <tr>

                                    <th
                                        className="text-center"
                                        style={{
                                            minWidth: "60px"
                                        }}
                                    >
                                        #
                                    </th>


                                    <th
                                        style={{
                                            minWidth: "220px"
                                        }}
                                    >
                                        User Name
                                    </th>


                                    <th
                                        style={{
                                            minWidth: "150px"
                                        }}
                                    >
                                        User ID
                                    </th>


                                    {/* Dynamic Enabled Papers */}

                                    {papers.map(
                                        (paper) => (

                                            <th
                                                key={
                                                    paper.id
                                                }
                                                className="
                                                    text-center
                                                "
                                                style={{
                                                    minWidth: "130px",
                                                    whiteSpace:
                                                        "nowrap"
                                                }}
                                                title={
                                                    paper.paper
                                                }
                                            >

                                                {paper.paper}

                                            </th>

                                        )
                                    )}


                                    {/* Action */}

                                    <th
                                        className="text-center"
                                        style={{
                                            minWidth: "110px"
                                        }}
                                    >
                                        Action
                                    </th>

                                </tr>

                            </thead>


                            {/* =================================================
                                Body
                            ================================================= */}

                            <tbody>

                                {paginatedUsers.length >
                                0 ? (

                                    paginatedUsers.map(
                                        (
                                            user,
                                            index
                                        ) => {


                                            /*
                                            |--------------------------------------------------------------------------
                                            | User Paper Map
                                            |--------------------------------------------------------------------------
                                            */

                                            const userPaperMap =
                                                {};


                                            if (
                                                Array.isArray(
                                                    user.papers
                                                )
                                            ) {

                                                user.papers.forEach(
                                                    (
                                                        paper
                                                    ) => {

                                                        const key =
                                                            String(
                                                                paper.paper ||
                                                                ""
                                                            )
                                                                .trim()
                                                                .toLowerCase();


                                                        userPaperMap[
                                                            key
                                                        ] =
                                                            Boolean(
                                                                paper.purchased
                                                            );

                                                    }
                                                );

                                            }


                                            return (

                                                <tr
                                                    key={
                                                        user.user_id ||
                                                        index
                                                    }
                                                >


                                                    {/* # */}

                                                    <td
                                                        className="
                                                            text-center
                                                        "
                                                    >

                                                        {
                                                            startIndex +
                                                            index +
                                                            1
                                                        }

                                                    </td>


                                                    {/* User Name */}

                                                    <td>

                                                        <strong>
                                                            {
                                                                user.fullname
                                                            }
                                                        </strong>

                                                    </td>


                                                    {/* User ID */}

                                                    <td>

                                                        <span
                                                            className="
                                                                badge
                                                                bg-light
                                                                text-dark
                                                                border
                                                            "
                                                        >

                                                            {
                                                                user.user_id
                                                            }

                                                        </span>

                                                    </td>


                                                    {/* Dynamic Papers */}

                                                    {papers.map(
                                                        (
                                                            paper
                                                        ) => {

                                                            const paperKey =
                                                                String(
                                                                    paper.paper ||
                                                                    ""
                                                                )
                                                                    .trim()
                                                                    .toLowerCase();


                                                            const purchased =
                                                                userPaperMap[
                                                                    paperKey
                                                                ] === true;


                                                            return (

                                                                <td
                                                                    key={
                                                                        paper.id
                                                                    }
                                                                    className="
                                                                        text-center
                                                                    "
                                                                >

                                                                    {purchased ? (

                                                                        <span
                                                                            className="
                                                                                text-success
                                                                                fw-bold
                                                                                fs-5
                                                                            "
                                                                            title="
                                                                                Purchased
                                                                            "
                                                                        >
                                                                            ✓
                                                                        </span>

                                                                    ) : (

                                                                        <span
                                                                            className="
                                                                                text-danger
                                                                                fw-bold
                                                                                fs-5
                                                                            "
                                                                            title="
                                                                                Not Purchased
                                                                            "
                                                                        >
                                                                            ✕
                                                                        </span>

                                                                    )}

                                                                </td>

                                                            );

                                                        }
                                                    )}


                                                    {/* =================================================
                                                        Edit Button
                                                    ================================================= */}

                                                    <td
                                                        className="
                                                            text-center
                                                        "
                                                    >

                                                        <Link
                                                            to={
                                                                `/edit-user/${encodeURIComponent(
                                                                    user.user_id
                                                                )}`
                                                            }
                                                            className="
                                                                btn
                                                                btn-sm
                                                                btn-outline-primary
                                                            "
                                                            title="Edit User Papers"
                                                        >

                                                            <i
                                                                className="
                                                                    fa
                                                                    fa-edit
                                                                    me-1
                                                                "
                                                            ></i>

                                                            Edit

                                                        </Link>

                                                    </td>

                                                </tr>

                                            );

                                        }
                                    )

                                ) : (

                                    <tr>

                                        <td
                                            colSpan={
                                                4 +
                                                papers.length
                                            }
                                            className="
                                                text-center
                                                py-5
                                                text-muted
                                            "
                                        >

                                            <i
                                                className="
                                                    fa
                                                    fa-users
                                                    fs-3
                                                    mb-3
                                                "
                                            ></i>


                                            <div>
                                                No users found.
                                            </div>

                                        </td>

                                    </tr>

                                )}

                            </tbody>

                        </table>

                    </div>

                </div>


                {/* =================================================
                    Pagination
                ================================================= */}

                {filteredUsers.length > 0 && (

                    <div
                        className="
                            card-footer
                            bg-white
                            d-flex
                            justify-content-between
                            align-items-center
                        "
                    >


                        {/* Showing */}

                        <div
                            className="text-muted"
                        >

                            Showing{" "}

                            <strong>
                                {startIndex + 1}
                            </strong>

                            {" "}to{" "}

                            <strong>
                                {Math.min(
                                    startIndex +
                                    itemsPerPage,
                                    filteredUsers.length
                                )}
                            </strong>

                            {" "}of{" "}

                            <strong>
                                {filteredUsers.length}
                            </strong>

                        </div>


                        {/* Buttons */}

                        <div
                            className="btn-group"
                        >

                            <button
                                type="button"
                                className="
                                    btn
                                    btn-outline-primary
                                "
                                disabled={
                                    currentPage === 1
                                }
                                onClick={
                                    handlePrevious
                                }
                            >
                                Previous
                            </button>


                            <button
                                type="button"
                                className="
                                    btn
                                    btn-primary
                                "
                                disabled
                            >

                                {currentPage}

                                {" / "}

                                {totalPages || 1}

                            </button>


                            <button
                                type="button"
                                className="
                                    btn
                                    btn-outline-primary
                                "
                                disabled={
                                    currentPage >=
                                    totalPages
                                }
                                onClick={
                                    handleNext
                                }
                            >
                                Next
                            </button>

                        </div>

                    </div>

                )}

            </div>

        </div>

    );

}


export default Users;